import React, { useEffect, useState, useRef, useCallback } from 'react';
import { getAdminToursList, getAdminToursCounts, getTourBySlug, createTour, updateTour, deleteTour, uploadImage } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Upload, Loader2, Calendar, MapPin, Clock, Utensils } from 'lucide-react';
import { toast } from 'sonner';
import { DepartureManager } from './DepartureManager';
import { CategorySelect, TourCategoryManager, TOUR_TYPE_LABELS } from './TourCategoryManager';
import { currencySymbol, tourCurrencySymbol, getTourCurrency } from '@/lib/utils';

export default function ToursManagement() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const requestIdRef = useRef(0);
  const [activeType, setActiveType] = useState('');
  const [counts, setCounts] = useState({});
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    short_description: '',
    price: '',
    duration: '',
    max_capacity: 40,
    image_url: '',
    category: '',
    tour_type: 'istanbul',
    currency: 'TRY',
    highlights: '',
    included: '',
    excluded: '',
    meeting_point: '',
    available_dates: '',
    is_featured: false,
    discount_percentage: 0,
    program: [],
    // Konaklamalı tur fiyatları
    price_single: '',
    price_triple: '',
    child_prices: [],
    tour_dates: []
  });

  // Child price template
  const emptyChildPrice = {
    age_min: 0,
    age_max: 6,
    price: 0,
    label: '0-6 Yaş'
  };

  // Tour date template
  const emptyTourDate = {
    start_date: '',
    end_date: '',
    status: 'available'
  };

  // Program day template
  const emptyProgramDay = {
    day: 1,
    title: '',
    description: '',
    time: '',
    location: '',
    meals: ''
  };

  // Predefined program templates
  const programTemplates = [
    {
      name: 'İstanbul Günübirlik Tur',
      days: [
        { day: 1, title: 'İstanbul Keşfi', description: 'Tarihi yarımadanın en önemli noktalarını ziyaret ediyoruz.', time: '09:00 - 17:00', location: 'Sultanahmet', meals: 'Öğle Yemeği Dahil' }
      ]
    },
    {
      name: 'Kapadokya 2 Günlük',
      days: [
        { day: 1, title: 'Kapadokya\'ya Varış', description: 'Uçuş sonrası otel transferi ve akşam yemeği.', time: '14:00 - 20:00', location: 'Nevşehir', meals: 'Akşam Yemeği' },
        { day: 2, title: 'Kapadokya Turu', description: 'Göreme Açık Hava Müzesi, Ürgüp, Avanos ve yeraltı şehirleri.', time: '08:00 - 18:00', location: 'Göreme', meals: 'Kahvaltı, Öğle Yemeği' }
      ]
    },
    {
      name: 'Ege 3 Günlük',
      days: [
        { day: 1, title: 'Efes Antik Kenti', description: 'Dünyanın en iyi korunmuş antik kentlerinden Efes\'i keşfedin.', time: '09:00 - 17:00', location: 'İzmir/Selçuk', meals: 'Öğle Yemeği' },
        { day: 2, title: 'Pamukkale', description: 'Beyaz travertenler ve Hierapolis antik kenti.', time: '08:00 - 18:00', location: 'Denizli', meals: 'Kahvaltı, Öğle Yemeği' },
        { day: 3, title: 'Şirince Köyü', description: 'Tarihi Rum köyü ve şarap tadımı.', time: '09:00 - 15:00', location: 'Şirince', meals: 'Kahvaltı' }
      ]
    },
    {
      name: 'Avrupa 5 Günlük',
      days: [
        { day: 1, title: 'Varış ve Şehir Turu', description: 'Havalimanı transferi ve şehir tanıtım turu.', time: '12:00 - 20:00', location: '', meals: 'Akşam Yemeği' },
        { day: 2, title: 'Tarihi Merkezler', description: 'Şehrin tarihi merkezlerini keşfedin.', time: '09:00 - 18:00', location: '', meals: 'Kahvaltı, Öğle Yemeği' },
        { day: 3, title: 'Müzeler ve Galeriler', description: 'Ünlü müzeler ve sanat galerileri.', time: '10:00 - 17:00', location: '', meals: 'Kahvaltı' },
        { day: 4, title: 'Serbest Zaman', description: 'Alışveriş ve serbest keşif.', time: 'Serbest', location: '', meals: 'Kahvaltı' },
        { day: 5, title: 'Dönüş', description: 'Havalimanı transferi ve uçuş.', time: '08:00 - 14:00', location: '', meals: 'Kahvaltı' }
      ]
    },
    {
      name: 'Yunanistan Adaları 7 Günlük',
      days: [
        { day: 1, title: 'İstanbul - Atina', description: 'Uçuş ve Atina şehir turu.', time: '07:00 - 20:00', location: 'Atina', meals: 'Akşam Yemeği' },
        { day: 2, title: 'Atina Akropolis', description: 'Akropolis ve Plaka bölgesi.', time: '09:00 - 18:00', location: 'Atina', meals: 'Kahvaltı, Öğle' },
        { day: 3, title: 'Santorini\'ye Geçiş', description: 'Feribot ile Santorini\'ye.', time: '08:00 - 16:00', location: 'Santorini', meals: 'Kahvaltı' },
        { day: 4, title: 'Santorini Keşfi', description: 'Oia ve Fira köyleri, gün batımı.', time: '10:00 - 22:00', location: 'Santorini', meals: 'Kahvaltı' },
        { day: 5, title: 'Mykonos\'a Geçiş', description: 'Feribot ile Mykonos\'a.', time: '09:00 - 15:00', location: 'Mykonos', meals: 'Kahvaltı' },
        { day: 6, title: 'Mykonos Plajları', description: 'Plaj günü ve gece hayatı.', time: 'Serbest', location: 'Mykonos', meals: 'Kahvaltı' },
        { day: 7, title: 'Dönüş', description: 'Atina üzerinden İstanbul\'a dönüş.', time: '06:00 - 18:00', location: 'Atina-İstanbul', meals: 'Kahvaltı' }
      ]
    }
  ];

  // Apply template
  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      program: template.days.map((d, i) => ({ ...d, day: i + 1 }))
    }));
    toast.success(`"${template.name}" şablonu uygulandı`);
  };

  const PAGE_SIZE = 12;

  const loadPage = useCallback(async (skip) => {
    if (skip !== 0 && loadingMoreRef.current) return;
    const requestId = ++requestIdRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const { data } = await getAdminToursList(skip, PAGE_SIZE, activeType);
      if (requestId !== requestIdRef.current) return;
      setTours(prev => (skip === 0 ? data.items : [...prev, ...data.items]));
      setHasMore(data.has_more);
      setTotal(data.total);
    } catch (error) {
      toast.error('Turlar yüklenirken hata oluştu');
    } finally {
      if (requestId === requestIdRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
        setLoading(false);
      }
    }
  }, [activeType]);

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await getAdminToursCounts();
      setCounts(data);
    } catch (error) {
      setCounts({});
    }
  }, []);

  const fetchTours = () => { loadCounts(); loadPage(0); };

  useEffect(() => { loadCounts(); }, [loadCounts]);

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadPage(tours.length);
    }, { rootMargin: '300px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, tours.length, loadPage]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await uploadImage(file);
      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Görsel yüklendi');
    } catch (error) {
      toast.error('Görsel yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: editingTour ? prev.slug : generateSlug(title)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category) {
      toast.error('Lütfen bir kategori seçin');
      return;
    }
    try {
      const isAccommodationTour = formData.tour_type === 'yurtici' || formData.tour_type === 'yurtdisi';
      
      const tourData = {
        ...formData,
        price: parseFloat(formData.price),
        max_capacity: parseInt(formData.max_capacity),
        discount_percentage: parseInt(formData.discount_percentage) || 0,
        highlights: formData.highlights.split('\n').filter(h => h.trim()),
        included: formData.included.split('\n').filter(i => i.trim()),
        excluded: formData.excluded.split('\n').filter(e => e.trim()),
        available_dates: formData.available_dates.split(/[,;\n]+/).map(d => d.trim()).filter(d => d),
        program: formData.program.filter(p => p.title.trim()),
        // Konaklamalı tur fiyatları
        price_single: isAccommodationTour && formData.price_single ? parseFloat(formData.price_single) : null,
        price_triple: isAccommodationTour && formData.price_triple ? parseFloat(formData.price_triple) : null,
        child_prices: isAccommodationTour ? formData.child_prices : [],
        tour_dates: isAccommodationTour ? formData.tour_dates : [],
        currency: formData.currency
      };

      if (editingTour) {
        await updateTour(editingTour.id, tourData);
        toast.success('Tur güncellendi');
      } else {
        await createTour(tourData);
        toast.success('Yeni tur eklendi');
      }

      setIsModalOpen(false);
      resetForm();
      fetchTours();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'İşlem başarısız');
      console.error(error);
    }
  };

  // Program Day handlers
  const addProgramDay = () => {
    const newDay = {
      ...emptyProgramDay,
      day: formData.program.length + 1
    };
    setFormData(prev => ({
      ...prev,
      program: [...prev.program, newDay]
    }));
  };

  const removeProgramDay = (index) => {
    setFormData(prev => ({
      ...prev,
      program: prev.program.filter((_, i) => i !== index).map((p, i) => ({ ...p, day: i + 1 }))
    }));
  };

  const updateProgramDay = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      program: prev.program.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const handleEdit = async (listTour) => {
    let tour;
    try {
      const { data } = await getTourBySlug(listTour.slug, 'tr');
      tour = data;
    } catch (error) {
      toast.error('Tur detayı yüklenemedi');
      return;
    }
    setEditingTour(tour);
    setFormData({
      title: tour.title,
      slug: tour.slug,
      description: tour.description,
      short_description: tour.short_description,
      price: tour.price.toString(),
      duration: tour.duration,
      max_capacity: tour.max_capacity,
      image_url: tour.image_url,
      category: tour.category,
      tour_type: tour.tour_type,
      currency: getTourCurrency(tour),
      highlights: tour.highlights?.join('\n') || '',
      included: tour.included?.join('\n') || '',
      excluded: tour.excluded?.join('\n') || '',
      meeting_point: tour.meeting_point,
      available_dates: tour.available_dates?.join(', ') || '',
      is_featured: tour.is_featured,
      discount_percentage: tour.discount_percentage,
      program: tour.program || [],
      // Konaklamalı tur fiyatları
      price_single: tour.price_single?.toString() || '',
      price_triple: tour.price_triple?.toString() || '',
      child_prices: tour.child_prices || [],
      tour_dates: tour.tour_dates || []
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu turu silmek istediğinizden emin misiniz?')) return;
    try {
      await deleteTour(id);
      toast.success('Tur silindi');
      fetchTours();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const resetForm = () => {
    setEditingTour(null);
    setFormData({
      title: '',
      slug: '',
      description: '',
      short_description: '',
      price: '',
      duration: '',
      max_capacity: 40,
      image_url: '',
      category: '',
      tour_type: activeType || 'istanbul',
      currency: activeType === 'yurtdisi' ? 'EUR' : 'TRY',
      highlights: '',
      included: '',
      excluded: '',
      meeting_point: '',
      available_dates: '',
      is_featured: false,
      discount_percentage: 0,
      program: [],
      price_single: '',
      price_triple: '',
      child_prices: [],
      tour_dates: []
    });
  };

  // Child price handlers
  const addChildPrice = () => {
    setFormData(prev => ({
      ...prev,
      child_prices: [...prev.child_prices, { ...emptyChildPrice }]
    }));
  };

  const removeChildPrice = (index) => {
    setFormData(prev => ({
      ...prev,
      child_prices: prev.child_prices.filter((_, i) => i !== index)
    }));
  };

  const updateChildPrice = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      child_prices: prev.child_prices.map((cp, i) => 
        i === index ? { ...cp, [field]: field === 'price' || field === 'age_min' || field === 'age_max' ? parseFloat(value) || 0 : value } : cp
      )
    }));
  };

  // Tour date handlers
  const addTourDate = () => {
    setFormData(prev => ({
      ...prev,
      tour_dates: [...prev.tour_dates, { ...emptyTourDate }]
    }));
  };

  const removeTourDate = (index) => {
    setFormData(prev => ({
      ...prev,
      tour_dates: prev.tour_dates.filter((_, i) => i !== index)
    }));
  };

  const updateTourDate = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tour_dates: prev.tour_dates.map((td, i) => 
        i === index ? { ...td, [field]: value } : td
      )
    }));
  };

  if (loading) return <div className="text-center py-12">Yükleniyor...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Tur Yönetimi <span data-testid="admin-tours-total" className="text-base font-normal text-gray-500">({total} tur)</span></h2>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus size={20} className="mr-2" /> Yeni Tur Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTour ? 'Tur Düzenle' : 'Yeni Tur Ekle'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tur Başlığı *</Label>
                  <Input name="title" value={formData.title} onChange={handleTitleChange} required />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input name="slug" value={formData.slug} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label>Fiyat ({currencySymbol(formData.currency)}) *</Label>
                  <Input name="price" type="number" value={formData.price} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label>Süre *</Label>
                  <Input name="duration" value={formData.duration} onChange={handleInputChange} placeholder="Ör: 6 Saat" required />
                </div>
                <div>
                  <Label>Kategori *</Label>
                  <CategorySelect tourType={formData.tour_type} value={formData.category} onChange={(value) => setFormData(prev => ({ ...prev, category: value }))} />
                </div>
                <div>
                  <Label>Tur Tipi *</Label>
                  <Select name="tour_type" value={formData.tour_type} onValueChange={(value) => setFormData(prev => ({ ...prev, tour_type: value, category: value === prev.tour_type ? prev.category : '', currency: editingTour ? prev.currency : (value === 'yurtdisi' ? 'EUR' : 'TRY') }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="istanbul">İstanbul</SelectItem>
                      <SelectItem value="yurtici">Yurtiçi</SelectItem>
                      <SelectItem value="yurtdisi">Yurtdışı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Para Birimi *</Label>
                  <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                    <SelectTrigger data-testid="tour-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TRY" data-testid="tour-currency-try">₺ Türk Lirası (TRY)</SelectItem>
                      <SelectItem value="EUR" data-testid="tour-currency-eur">€ Euro (EUR)</SelectItem>
                      <SelectItem value="USD" data-testid="tour-currency-usd">$ Dolar (USD)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max Kapasite</Label>
                  <Input name="max_capacity" type="number" value={formData.max_capacity} onChange={handleInputChange} />
                </div>
                <div>
                  <Label>İndirim (%)</Label>
                  <Input name="discount_percentage" type="number" value={formData.discount_percentage} onChange={handleInputChange} />
                </div>
              </div>
              <div>
                <Label>Kısa Açıklama *</Label>
                <Input name="short_description" value={formData.short_description} onChange={handleInputChange} required />
              </div>
              <div>
                <Label>Detaylı Açıklama *</Label>
                <Textarea name="description" value={formData.description} onChange={handleInputChange} rows={4} required />
              </div>
              <div>
                <Label>Görsel *</Label>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <Input 
                      name="image_url" 
                      value={formData.image_url} 
                      onChange={handleInputChange} 
                      placeholder="Görsel URL veya yükle"
                      className="flex-1"
                    />
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <Button type="button" variant="outline" className="h-10" disabled={uploading} asChild>
                        <span>
                          {uploading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Upload size={16} className="mr-2" />}
                          {uploading ? 'Yükleniyor...' : 'Yükle'}
                        </span>
                      </Button>
                    </label>
                  </div>
                  {formData.image_url && (
                    <img src={formData.image_url} alt="Önizleme" className="h-32 w-auto object-cover rounded-lg border" />
                  )}
                </div>
              </div>
              <div>
                <Label>Buluşma Noktası *</Label>
                <Input name="meeting_point" value={formData.meeting_point} onChange={handleInputChange} required />
              </div>
              <div>
                <Label>Öne Çıkanlar (Her satıra bir madde)</Label>
                <Textarea name="highlights" value={formData.highlights} onChange={handleInputChange} rows={3} />
              </div>
              <div>
                <Label>Dahil Olanlar (Her satıra bir madde)</Label>
                <Textarea name="included" value={formData.included} onChange={handleInputChange} rows={3} />
              </div>
              <div>
                <Label>Dahil Olmayanlar (Her satıra bir madde)</Label>
                <Textarea name="excluded" value={formData.excluded} onChange={handleInputChange} rows={3} />
              </div>
              <div>
                <Label>Tur Tarihleri (virgülle veya her satıra bir tarih: 22.11.2026, 2026-11-29, 6 Aralık 2026)</Label>
                <Textarea name="available_dates" value={formData.available_dates} onChange={handleInputChange} rows={2} data-testid="tour-available-dates-input" />
              </div>

              {/* Konaklamalı Tur Fiyatlandırma - Sadece yurtici/yurtdisi için */}
              {(formData.tour_type === 'yurtici' || formData.tour_type === 'yurtdisi') && (
                <div className="border-t pt-4 mt-4 bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="bg-blue-500 text-white p-2 rounded-full">
                      <Calendar size={18} />
                    </div>
                    <div>
                      <Label className="text-lg font-bold text-blue-800">Konaklamalı Tur Fiyatlandırma</Label>
                      <p className="text-sm text-blue-600">Oda tiplerine göre fiyat ve tur tarihleri</p>
                    </div>
                  </div>

                  {/* Oda Fiyatları */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <Label className="text-blue-700">DB Kişi Başı (Ana Fiyat) *</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          name="price" 
                          type="number" 
                          value={formData.price} 
                          onChange={handleInputChange} 
                          placeholder="0"
                          className="bg-white"
                          required
                        />
                        <span className="text-gray-500">{currencySymbol(formData.currency)}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Web sitesinde görünecek</p>
                    </div>
                    <div>
                      <Label className="text-blue-700">Single Oda Farkı</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          name="price_single" 
                          type="number" 
                          value={formData.price_single} 
                          onChange={handleInputChange} 
                          placeholder="0"
                          className="bg-white"
                        />
                        <span className="text-gray-500">{currencySymbol(formData.currency)}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Tek kişilik oda fark ücreti</p>
                    </div>
                    <div>
                      <Label className="text-blue-700">Triple Kişi Başı</Label>
                      <div className="flex items-center gap-2">
                        <Input 
                          name="price_triple" 
                          type="number" 
                          value={formData.price_triple} 
                          onChange={handleInputChange} 
                          placeholder="0"
                          className="bg-white"
                        />
                        <span className="text-gray-500">{currencySymbol(formData.currency)}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">3 kişilik oda kişi başı</p>
                    </div>
                  </div>

                  {/* Çocuk Fiyatları */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-blue-700">Çocuk Fiyatları</Label>
                      <Button 
                        type="button" 
                        onClick={addChildPrice}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-600 hover:bg-blue-100"
                      >
                        <Plus size={14} className="mr-1" /> Yaş Grubu Ekle
                      </Button>
                    </div>
                    {formData.child_prices.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">Henüz çocuk fiyatı eklenmedi</p>
                    ) : (
                      <div className="space-y-2">
                        {formData.child_prices.map((cp, index) => (
                          <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                            <Input
                              type="number"
                              value={cp.age_min}
                              onChange={(e) => updateChildPrice(index, 'age_min', e.target.value)}
                              className="w-16"
                              placeholder="Min"
                            />
                            <span>-</span>
                            <Input
                              type="number"
                              value={cp.age_max}
                              onChange={(e) => updateChildPrice(index, 'age_max', e.target.value)}
                              className="w-16"
                              placeholder="Max"
                            />
                            <span className="text-gray-500">yaş:</span>
                            <Input
                              type="number"
                              value={cp.price}
                              onChange={(e) => updateChildPrice(index, 'price', e.target.value)}
                              className="w-24"
                              placeholder="Fiyat"
                            />
                            <span className="text-gray-500">{currencySymbol(formData.currency)}</span>
                            <Input
                              value={cp.label}
                              onChange={(e) => updateChildPrice(index, 'label', e.target.value)}
                              className="flex-1"
                              placeholder="Etiket (ör: 0-2 Yaş Ücretsiz)"
                            />
                            <Button
                              type="button"
                              onClick={() => removeChildPrice(index)}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Tur Tarihleri */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-blue-700">Tur Tarihleri</Label>
                      <Button 
                        type="button" 
                        onClick={addTourDate}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-600 hover:bg-blue-100"
                      >
                        <Plus size={14} className="mr-1" /> Tarih Ekle
                      </Button>
                    </div>
                    {formData.tour_dates.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">Henüz tur tarihi eklenmedi</p>
                    ) : (
                      <div className="space-y-2">
                        {formData.tour_dates.map((td, index) => (
                          <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-gray-500">Başlangıç:</Label>
                              <Input
                                type="date"
                                value={td.start_date}
                                onChange={(e) => updateTourDate(index, 'start_date', e.target.value)}
                                className="w-40"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-gray-500">Bitiş:</Label>
                              <Input
                                type="date"
                                value={td.end_date}
                                onChange={(e) => updateTourDate(index, 'end_date', e.target.value)}
                                className="w-40"
                              />
                            </div>
                            <Select 
                              value={td.status} 
                              onValueChange={(value) => updateTourDate(index, 'status', value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="available">Müsait</SelectItem>
                                <SelectItem value="limited">Az Yer</SelectItem>
                                <SelectItem value="full">Dolu</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              onClick={() => removeTourDate(index)}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Daily Program / Itinerary Editor */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-lg font-bold">Günlük Program</Label>
                    <p className="text-sm text-gray-500">Tur programının gün gün detaylarını ekleyin</p>
                  </div>
                  <div className="flex gap-2">
                    <Select onValueChange={(value) => applyTemplate(programTemplates.find(t => t.name === value))}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Şablon Seç" />
                      </SelectTrigger>
                      <SelectContent>
                        {programTemplates.map((template) => (
                          <SelectItem key={template.name} value={template.name}>
                            {template.name} ({template.days.length} gün)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button" 
                      onClick={addProgramDay}
                      className="bg-green-500 hover:bg-green-600 text-white"
                      size="sm"
                    >
                      <Plus size={16} className="mr-1" /> Gün Ekle
                    </Button>
                  </div>
                </div>

                {formData.program.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <Calendar className="mx-auto text-gray-400 mb-2" size={32} />
                    <p className="text-gray-500">Henüz program eklenmedi</p>
                    <Button 
                      type="button" 
                      onClick={addProgramDay}
                      variant="outline"
                      className="mt-3"
                      size="sm"
                    >
                      <Plus size={16} className="mr-1" /> İlk Günü Ekle
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {formData.program.map((day, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-orange-100 text-orange-700">
                            <Calendar size={14} className="mr-1" />
                            {day.day}. Gün
                          </span>
                          <Button 
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeProgramDay(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="md:col-span-2">
                            <Label className="text-xs text-gray-500">Gün Başlığı *</Label>
                            <Input 
                              value={day.title}
                              onChange={(e) => updateProgramDay(index, 'title', e.target.value)}
                              placeholder="Ör: İstanbul'a Varış & Sultanahmet Keşfi"
                            />
                          </div>
                          
                          <div className="md:col-span-2">
                            <Label className="text-xs text-gray-500">Açıklama</Label>
                            <Textarea 
                              value={day.description}
                              onChange={(e) => updateProgramDay(index, 'description', e.target.value)}
                              placeholder="Bu günün detaylı açıklaması..."
                              rows={2}
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock size={12} /> Saat
                            </Label>
                            <Input 
                              value={day.time}
                              onChange={(e) => updateProgramDay(index, 'time', e.target.value)}
                              placeholder="Ör: 09:00 - 18:00"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs text-gray-500 flex items-center gap-1">
                              <MapPin size={12} /> Konum
                            </Label>
                            <Input 
                              value={day.location}
                              onChange={(e) => updateProgramDay(index, 'location', e.target.value)}
                              placeholder="Ör: Sultanahmet, Fatih"
                            />
                          </div>
                          
                          <div className="md:col-span-2">
                            <Label className="text-xs text-gray-500 flex items-center gap-1">
                              <Utensils size={12} /> Yemekler
                            </Label>
                            <Input 
                              value={day.meals}
                              onChange={(e) => updateProgramDay(index, 'meals', e.target.value)}
                              placeholder="Ör: Kahvaltı, Öğle Yemeği"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <input type="checkbox" name="is_featured" checked={formData.is_featured} onChange={handleInputChange} className="w-4 h-4" />
                <Label>Öne Çıkan Tur</Label>
              </div>
              {editingTour && <DepartureManager tourId={editingTour.id} />}
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">
                  {editingTour ? 'Güncelle' : 'Ekle'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setIsModalOpen(false); resetForm(); }}>
                  İptal
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap gap-2" data-testid="admin-tours-type-tabs">
          {[['', 'Tümü', counts.all], ...Object.entries(TOUR_TYPE_LABELS).map(([k, label]) => [k, label, counts[k]])].map(([key, label, count]) => (
            <button key={key || 'all'} type="button" onClick={() => setActiveType(key)} data-testid={`admin-tours-tab-${key || 'all'}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${activeType === key ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300'}`}>
              {label} <span className="opacity-75">({count ?? 0})</span>
            </button>
          ))}
        </div>
        {activeType && (
          <Button variant="outline" onClick={() => setCategoryManagerOpen(true)} data-testid="admin-manage-categories-button">
            Kategorileri Yönet
          </Button>
        )}
      </div>
      {activeType && <TourCategoryManager tourType={activeType} open={categoryManagerOpen} onOpenChange={setCategoryManagerOpen} />}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tours.length === 0 && !loadingMore && <p className="text-gray-500 col-span-full" data-testid="admin-tours-empty">Bu bölümde henüz tur yok</p>}
        {tours.map((tour) => (
          <div key={tour.id} data-testid={`admin-tour-card-${tour.id}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <img src={tour.image_url} alt={tour.title} loading="lazy" decoding="async" className="w-full h-48 object-cover bg-gray-100" />
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-ui font-bold ${
                  tour.tour_type === 'istanbul' ? 'bg-blue-100 text-blue-700' :
                  tour.tour_type === 'yurtici' ? 'bg-green-100 text-green-700' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  {tour.tour_type === 'istanbul' ? 'İstanbul' :
                   tour.tour_type === 'yurtici' ? 'Yurtiçi' : 'Yurtdışı'}
                </span>
                {tour.is_featured && <span className="px-3 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-700">Öne Çıkan</span>}
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">{tour.title}</h3>
              <p className="text-sm text-gray-600 mb-4">{tour.category}</p>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xl font-bold font-ui text-orange-500">{tourCurrencySymbol(tour)}{tour.price}</span>
                <span className="text-sm text-gray-500">{tour.duration}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleEdit(tour)} data-testid={`admin-tour-edit-${tour.id}`} className="flex-1 bg-blue-500 hover:bg-blue-600">
                  <Edit size={16} className="mr-1" /> Düzenle
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(tour.id)} data-testid={`admin-tour-delete-${tour.id}`} className="flex-1">
                  <Trash2 size={16} className="mr-1" /> Sil
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div ref={sentinelRef} data-testid="admin-tours-sentinel" className="py-8 text-center text-sm text-gray-500">
        {loadingMore && <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Daha fazla tur yükleniyor...</span>}
        {!hasMore && tours.length > 0 && <span data-testid="admin-tours-end">Tüm turlar yüklendi</span>}
      </div>
    </div>
  );
}